// @flow
import axios, { CancelToken, Cancel, isCancel } from 'axios';
import type { CancelToken as CancelTokenType } from 'axios';

import { get, set, hash } from './cache';

import type { Request, Response } from '../types';

type Props = {|
  ...Request,
  cancelToken?: CancelTokenType,
|};

const request = async ({
  url,
  template,
  page = 1,
  params = {},
  cancelToken = new CancelToken(() => {}),
}: Props): Promise<Response> => {
  // Create cache key
  const key = hash({
    url,
    template,
    page,
    params,
  });

  // Check cache
  const cacheHit = await get(key);

  // Return from cahce
  if (cacheHit) {
    return cacheHit;
  }

  let headers;
  let payload;

  try {
    // Create req
    const { data } = await axios.get(url, {
      params: {
        ...params,
        view: template,
        page,
      },
      transformResponse: [
        res => {
          try {
            return JSON.parse(res);
          } catch (e) {
            throw new Error('JSON parsing failed');
          }
        },
      ],
      cancelToken,
    });

    headers = {
      size: data.size,
      total: data.total,
      page: data.page,
      pages: data.pages,
    };

    ({ payload } = data);
  } catch (e) {
    // Canceled - not error
    if (isCancel(e)) {
      throw new Cancel('Uncaught cancellation error');
    }
    // Format network errors etc.
    throw new Error(
      `Failed to fetch '${url}?view=${template}': ${e.message || e}`,
    );
  }

  const response = {
    headers,
    payload,
  };

  // Cache response & ignore any errors
  if (page <= headers.pages) {
    set(key, response).catch(() => undefined);
  }

  return response;
};

export default request;
